import { inject, Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AbstractModelService, AbstractModelServiceEnv, buildValue, findValue } from '@finra/rampdi-forms-common';
import { environment } from '../../environments/environment';
import { InstanceService } from './instance.service';
import { NavigationDataService } from './navigation-data.service';
import { map, catchError, of, Observable, tap } from 'rxjs';
import { NMLSAPIService } from '../cloudwell-checklist-api/client';
import { DisplayRulesService } from './display-rules.service';

@Injectable({
  providedIn: 'root',
})
export class ModelService extends AbstractModelService {
  CBC_NAV_ITEMS = ['criminalBackgroundCheck'];
  LICENSE_NAV_ITEMS = ['licenses', 'formViewLicenses'];

  cloudwell = inject(NMLSAPIService);
  cbcData: any;

  constructor(
    NS: NavigationDataService,
    router: Router,
    protected IS: InstanceService,
    protected DRS: DisplayRulesService,
  ) {
    super(NS, router);
  }

  get environment(): AbstractModelServiceEnv {
    return environment;
  }

  /**
   * Process CBC request and return updated result
   */
  private processCBCRequest(subjectId: number, licenses: any[], result: any, navId?: any, isReadonly?: boolean) {
    // CBC section not available in MU3
    return of(result);
  }

  /**
   * Update CBC data
   */
  private updateCBCData(response: any, result: any, navId?: any, subjectId?: any): any {
    this.cbcData = response.data;
    if (response?.data?.checkCBCRequirement?.reason) {
      const cbcData = {
        requestCBC: false,
        cbcMethod: '',
        individualBiometrics: result.individualBiometrics,
      };
      this.IS.addDataChangesToInstance(cbcData, ['criminalBackgroundCheck']);
      return cbcData;
    }
    return result;
  }

  /**
   * Handle CBC processing errors
   */
  private handleCBCError(error: any, result: any): Observable<any> {
    console.error('Error in CBC request:', error);
    return of(result);
  }

  /**
   * @description Processes route resolution for different navigation items
   * @param {ActivatedRouteSnapshot} route - The activated route snapshot
   * @param {RouterStateSnapshot} state - The router state snapshot
   * @param {any} instance - The current instance data
   * @param {any} supportingData - Supporting data for the form
   * @param {any} filingTypeCode - The filing type code
   * @param {any} navItem - The navigation item
   * @param {string} navId - The navigation ID
   * @returns {any} The processed data for the form view
   */
  processResolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    instance: any,
    supportingData: any,
    filingTypeCode: any,
    navItem: any,
    navId: string,
  ) {
    /**
     * Overwriting default IMPL of navitem/navid to get the proper ones for Mu4's license requirements sections
     */
    navId = route.params.sectionId + (route.params.licenseTypeCode ? `;${route.params.licenseTypeCode}` : '');
    navItem = this.NS.getNavItem(`${route.params.viewType}.${filingTypeCode}`, navId);
    if (!navItem) {
      console.error(
        `No navigation data found for viewType ${route.params.viewType}, filingTypeCode ${filingTypeCode}, navId ${navId}`,
      );
    }

    if (navItem) {
      let result = findValue(instance?.collectedData, navItem?.modelPath || [navItem.id]);
      if (!result) {
        if (navItem?.modelPath) {
          result = buildValue(instance?.collectedData, navItem?.modelPath);
        } else {
          result = {};
        }
      }

      const subjectId = instance?.instanceMetadata?.target?.['subjectId'];
      const licenses = instance?.collectedData?.licenses;
      const filingOrgClass: string = instance?.instanceMetadata?.target?.user?.orgClass;
      const filingEntityId: number = instance?.instanceMetadata?.target?.entityId;

      try {
        if (navItem.id.includes('guidance')) {
          return this.cloudwell
            ?.nMLSAPILicenseTypeAndRequirementsNmlsIdChecklistTypeGet(
              route?.params?.licenseTypeCode,
              navItem?.data?.type,
            )
            ?.pipe(
              map((requirements) => {
                result['cloudwell'] = requirements;
                return result;
              }),
              catchError((error) => {
                if (error.status === 404) {
                  const licenseTypeSubString = navItem?.title?.substring(0, navItem.title.indexOf(':'));
                  const licenseTypeCode = licenseTypeSubString.includes('-')
                    ? navItem?.title?.substring(0, navItem?.title?.indexOf(':'))?.replace(/-/g, '')
                    : licenseTypeSubString;
                  return this.cloudwell?.nMLSAPILicenseTypeNmlsStateAgenciesGet(licenseTypeCode)?.pipe(
                    map((val) => {
                      result['cloudwell'] = {
                        agencyLongName: val.agencyLongName,
                        stateAgencyContactInformation: val.contactInformation,
                        //  requirements:[],
                        stateAgency: '',
                        generalLicenseInformation:
                          "<div style='font-weight: 600; font-size: 18px;'>Additional license requirements and notices have not been provided for this license. Proceed to complete the MU3 form.</div>",
                      };
                      return result;
                    }),
                    catchError((err) => {
                      return of(result);
                    }),
                  );
                } else {
                  console.error('Error:', error);
                  return of(result);
                }
              }),
            );
        } else if (this.CBC_NAV_ITEMS.includes(navItem?.id)) {
          const isReadonly = this.DRS.getCurrentDisplayRules()?.readOnlyMode;
          return this.processCBCRequest(subjectId, licenses, result, navItem?.id, isReadonly).pipe(
            tap((data) => Object.assign(result, data)),
            map((d) => result),
          );
        } else {
          return result;
        }
      } catch (error) {
        console.error('Error processing data:', error);
        return result || {};
      }
    }
  }
}
